import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export interface MediaPermissions {
  canView: boolean
  canControl: boolean
  canAddToQueue: boolean
  canRemoveFromQueue: boolean
  isOwner: boolean
  role: 'owner' | 'admin' | 'moderator' | 'member' | null
}

interface UseMediaPermissionsOptions {
  spaceId: string
  userId: string
  channelId?: string
}

/**
 * Hook to check user's media control permissions in a space
 *
 * Permission levels:
 * - View: All space members can view media playback
 * - Add to Queue: All space members can add items to the queue
 * - Control: Space owner OR admins/moderators with can_control_media = true
 * - Remove from Queue: Users can remove their own items, or admins can remove any
 */
export function useMediaPermissions({
  spaceId,
  userId,
  channelId
}: UseMediaPermissionsOptions) {
  const [permissions, setPermissions] = useState<MediaPermissions>({
    canView: false,
    canControl: false,
    canAddToQueue: false,
    canRemoveFromQueue: false,
    isOwner: false,
    role: null
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (spaceId && userId) {
      checkPermissions()
    }
  }, [spaceId, userId, channelId])

  const checkPermissions = async () => {
    try {
      setLoading(true)

      // Fetch space info and user's membership
      const { data: spaceData, error: spaceError } = await supabase
        .from('spaces')
        .select('id, owner_id')
        .eq('id', spaceId)
        .single()

      if (spaceError) {
        console.error('Error fetching space:', spaceError)
        setPermissions({
          canView: false,
          canControl: false,
          canAddToQueue: false,
          canRemoveFromQueue: false,
          isOwner: false,
          role: null
        })
        return
      }

      // Check if user is the space owner
      const isOwner = spaceData.owner_id === userId

      // Fetch user's membership info
      const { data: memberData, error: memberError } = await supabase
        .from('space_members')
        .select('role, can_control_media')
        .eq('space_id', spaceId)
        .eq('user_id', userId)
        .maybeSingle()

      if (memberError) {
        console.error('Error fetching membership:', memberError)
        setPermissions({
          canView: false,
          canControl: false,
          canAddToQueue: false,
          canRemoveFromQueue: false,
          isOwner: false,
          role: null
        })
        return
      }

      // If not a member and not the owner, no permissions
      if (!memberData && !isOwner) {
        setPermissions({
          canView: false,
          canControl: false,
          canAddToQueue: false,
          canRemoveFromQueue: false,
          isOwner: false,
          role: null
        })
        return
      }

      const role = memberData?.role || (isOwner ? 'owner' : null)
      const canControlMedia = memberData?.can_control_media || false

      // Determine permissions
      const canView = true // All members can view
      const canAddToQueue = true // All members can add to queue

      // Only owner OR admins/moderators with can_control_media permission can control
      const canControl = isOwner || (
        (role === 'admin' || role === 'moderator') && canControlMedia
      )

      // Admins and moderators with control can remove any queue item
      const canRemoveFromQueue = canControl

      setPermissions({
        canView,
        canControl,
        canAddToQueue,
        canRemoveFromQueue,
        isOwner,
        role: role as MediaPermissions['role']
      })
    } catch (err) {
      console.error('Error checking permissions:', err)
      setPermissions({
        canView: false,
        canControl: false,
        canAddToQueue: false,
        canRemoveFromQueue: false,
        isOwner: false,
        role: null
      })
    } finally {
      setLoading(false)
    }
  }

  // Helper to check if a specific channel is a media channel
  const isMediaChannel = async (checkChannelId?: string) => {
    const targetChannelId = checkChannelId || channelId
    if (!targetChannelId) return false

    try {
      const { data, error } = await supabase
        .from('channels')
        .select('type')
        .eq('id', targetChannelId)
        .single()

      if (error) {
        console.error('Error checking channel type:', error)
        return false
      }

      return data?.type === 'media'
    } catch (err) {
      console.error('Error checking channel type:', err)
      return false
    }
  }

  // Refresh permissions (useful after role changes)
  const refresh = () => {
    checkPermissions()
  }

  return {
    permissions,
    loading,
    isMediaChannel,
    refresh
  }
}

/**
 * Simple permission check without hook (useful for one-time checks)
 */
export async function checkMediaPermissions(
  spaceId: string,
  userId: string
): Promise<MediaPermissions> {
  try {
    // Fetch space info
    const { data: spaceData, error: spaceError } = await supabase
      .from('spaces')
      .select('id, owner_id')
      .eq('id', spaceId)
      .single()

    if (spaceError) throw spaceError

    const isOwner = spaceData.owner_id === userId

    // Fetch membership
    const { data: memberData, error: memberError } = await supabase
      .from('space_members')
      .select('role, can_control_media')
      .eq('space_id', spaceId)
      .eq('user_id', userId)
      .maybeSingle()

    if (memberError) throw memberError

    if (!memberData && !isOwner) {
      return {
        canView: false,
        canControl: false,
        canAddToQueue: false,
        canRemoveFromQueue: false,
        isOwner: false,
        role: null
      }
    }

    const role = memberData?.role || (isOwner ? 'owner' : null)
    const canControlMedia = memberData?.can_control_media || false

    const canView = true
    const canAddToQueue = true
    const canControl = isOwner || (
      (role === 'admin' || role === 'moderator') && canControlMedia
    )
    const canRemoveFromQueue = canControl

    return {
      canView,
      canControl,
      canAddToQueue,
      canRemoveFromQueue,
      isOwner,
      role: role as MediaPermissions['role']
    }
  } catch (err) {
    console.error('Error checking permissions:', err)
    return {
      canView: false,
      canControl: false,
      canAddToQueue: false,
      canRemoveFromQueue: false,
      isOwner: false,
      role: null
    }
  }
}
